import { Controller } from "@hotwired/stimulus"

// 汎用ドロップダウン。トリガークリックでメニューを開閉し、外側クリックで閉じる。
// 使い方:
//   <div data-controller="dropdown" data-action="click@window->dropdown#hide">
//     <button data-action="dropdown#toggle">...</button>
//     <div data-dropdown-target="menu" class="hidden">...</div>
//   </div>
export default class extends Controller<HTMLElement> {
  static targets = ["menu", "trigger"]
  static values = { placement: String }

  declare readonly menuTarget: HTMLElement
  // trigger は任意。指定した場合だけ aria-expanded を開閉に追随させる
  declare readonly triggerTarget: HTMLElement
  declare readonly hasTriggerTarget: boolean
  declare readonly placementValue: string

  private scrollContainer: Element | null = null
  private boundHide?: (event: Event) => void

  connect() {
    this.scrollContainer = this.element.closest(".overflow-x-auto")
    this.boundHide = this.hide.bind(this)
    this.scrollContainer?.addEventListener("scroll", this.boundHide)
  }

  disconnect() {
    if (this.boundHide) this.scrollContainer?.removeEventListener("scroll", this.boundHide)
  }

  toggle(event: Event) {
    if (this.placementValue === "fixed") {
      const opening = this.menuTarget.classList.contains("hidden")
      this.menuTarget.classList.toggle("hidden")
      if (opening) this.placeFixedMenu(event.currentTarget as HTMLElement)
      this.syncExpanded()
      return
    }

    this.menuTarget.classList.toggle("hidden")
    this.syncExpanded()
  }

  // 外側クリックで閉じる（root要素の外がクリックされたときだけ）
  hide(event: Event) {
    if (event.type === "scroll" || event.type === "resize") {
      this.menuTarget.classList.add("hidden")
      this.syncExpanded()
      return
    }

    const target = event.target
    if (!(target instanceof Node)) return

    if (!this.element.contains(target)) {
      this.menuTarget.classList.add("hidden")
      this.syncExpanded()
    }
  }

  private syncExpanded() {
    if (!this.hasTriggerTarget) return

    const open = !this.menuTarget.classList.contains("hidden")
    this.triggerTarget.setAttribute("aria-expanded", String(open))
  }

  placeFixedMenu(trigger: HTMLElement) {
    const triggerRect = trigger.getBoundingClientRect()
    const menuWidth = this.menuTarget.offsetWidth
    const menuHeight = this.menuTarget.offsetHeight
    const viewportPadding = 8
    const left = Math.min(
      Math.max(triggerRect.right - menuWidth, viewportPadding),
      window.innerWidth - menuWidth - viewportPadding
    )
    let top = triggerRect.bottom + viewportPadding

    if (window.innerHeight - triggerRect.bottom < menuHeight + viewportPadding) {
      top = triggerRect.top - menuHeight - viewportPadding
    }

    const maxTop = Math.max(viewportPadding, window.innerHeight - menuHeight - viewportPadding)
    top = Math.min(Math.max(top, viewportPadding), maxTop)

    Object.assign(this.menuTarget.style, {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`
    })
  }
}
